import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  Upload,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Mail,
  Download,
  CreditCard,
  AlertCircle,
  ArrowLeft,
  Loader
} from 'lucide-react';
import PdfViewer from '../components/PdfViewer';
import { usePackageDetail } from '../hooks/usePackageDetail';
import { useFileUpload } from '../hooks/useFileUpload';
import { CourierPackageApi, MailBankApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../shared/api-config';
import { Button, InputField } from '@shared/components';
import { useDarkMode } from '../context/DarkModeContext';
import { useSelector } from 'react-redux';
import { getCourier, getIsReviewsEnabled } from '@state/courierSlice';
import { formatWeightDisplay } from '../utils/weightUtils';
import { useTrackEvent } from '../utils/track';
import { buildPaymentInitiatedParams } from '../utils/payment';

const PackageDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isReviewsEnabled = useSelector(getIsReviewsEnabled);
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const courier = useSelector(getCourier);
  const track = useTrackEvent();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [invoiceExpanded, setInvoiceExpanded] = useState(true);
  const [receiptExpanded, setReceiptExpanded] = useState(true);
  const [invoiceDetailsExpanded, setInvoiceDetailsExpanded] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const { uploadFiles, isUploading, error: uploadError } = useFileUpload();
  const [packageValue, setPackageValue] = useState<number | ''>('');
  const amountInputRef = useRef<HTMLInputElement>(null);

  const {
    package: packageData,
    isLoading,
    error,
    refresh,
    sendStatusAlert,
    isEmailSending,
    emailError,
    getPaymentLink,
    paymentGateway,
    hasPaymentGateway
  } = usePackageDetail(id || '');

  useEffect(() => {
    if (id) {
      refresh();
    }
  }, [id, refresh]);

  // Check if the package status allows having an invoice
  const canHaveInvoice = (status?: string): boolean => {
    return ['Ready', 'Out for Delivery', 'Delivered'].includes(status || '');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles(prevFiles => {
        // Filter out duplicate files based on name and size
        const existingFileKeys = new Set(prevFiles.map(file => `${file.name}-${file.size}`));
        const uniqueNewFiles = newFiles.filter(file =>
          !existingFileKeys.has(`${file.name}-${file.size}`)
        );
        return [...prevFiles, ...uniqueNewFiles];
      });
    }
    // Clear the input so the same files can be selected again if needed
    e.target.value = '';
  };

  const handleBack = () => {
    navigate('/packages');
  };

  const toggleInvoiceSection = () => {
    setInvoiceExpanded(!invoiceExpanded);
  };

  const toggleReceiptSection = () => {
    setReceiptExpanded(!receiptExpanded);
  };

  const toggleInvoiceDetails = () => {
    setInvoiceDetailsExpanded(!invoiceDetailsExpanded);
  };

  const handleSendEmail = async () => {
    if (!packageData?.id) return;

    try {
      const success = await sendStatusAlert(['email', 'whatsapp']);
      if (success) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 3000);
      }
    } catch (error) {
      console.error('Error sending status alert:', error);
    }
  };

  const handleUploadReceipt = async () => {
    if (selectedFiles.length === 0 || !packageData?.id || !packageData.courierId) return;

    // Read value directly from the DOM input to avoid stale React state/closure issues
    const domValue = amountInputRef.current?.value;
    const valueToSend = domValue && domValue !== '' ? Number(domValue) : (packageData.value ?? 0);

    try {
      const uploadedFiles = await uploadFiles(
        selectedFiles,
        'invoices',
        packageData.courierId.toString(),
        packageData.id.toString(),
        packageData.attachedFile
      );

      const packageApi = new CourierPackageApi(apiConfig());
      await packageApi.courierPackageUpdateAttachments({
        packageId: packageData.id.toString(),
        value: valueToSend,
        attachments: [...(packageData.attachedFile || []), ...uploadedFiles.map((file) => file.name)],
      });

      setSelectedFiles([]);
      setPackageValue('');
      await refresh();
      track('package_invoice_uploaded', {
        package_id: packageData.id,
        file_count: uploadedFiles.length,
      });
      handleSendStatusAlert().catch((e) => {
        console.error('Error sending status alert:', e);
      });
    } catch (error) {
      console.error('Error uploading receipt:', error);
    }
  };

  const handleSendStatusAlert = async () => {

    if (!packageData?.id) return;
    const mailBankApi = new MailBankApi(apiConfig());
    await mailBankApi.sendInvoiceUploadedAlert({
      packageId: packageData.id.toString(),
    });
  }



  const handleViewFile = (fileName: string) => {
    if (!packageData?.courierId) return;
    const url = `${import.meta.env.VITE_API_BASE_URL}/Uploads/invoices-${packageData.courierId}/download/${fileName}`;
    window.open(url, '_blank');
  };

  const handleDeleteFile = async (fileName: string) => {
    if (!packageData?.id) return;

    // Ask for confirmation before deleting
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${fileName}"? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const packageApi = new CourierPackageApi(apiConfig());
      const currentFiles = packageData.attachedFile || [];
      const updatedFiles = currentFiles.filter(file => file !== fileName);

      await packageApi.courierPackageUpdateAttachments({
        packageId: packageData.id.toString(),
        value: packageData.value || 0,
        attachments: updatedFiles,
      });

      refresh();
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const handlePayInvoice = () => {
    const paymentLink = getPaymentLink();
    if (!paymentLink) return;
    track(
      'payment_initiated',
      buildPaymentInitiatedParams({
        value: packageData?.invoice?.amount ?? 0,
        courierSetting: courier?.courierSetting,
        paymentGateway,
        invoiceCount: 1,
      })
    );
    window.open(paymentLink, '_blank');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-2 text-gray-500 dark:text-gray-400">Loading package details...</p>
        </div>
      </div>
    );
  }

  if (error || !packageData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Failed to load package
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {error?.message || 'Package not found'}
          </p>
          <div className="space-y-2">
            <button
              onClick={refresh}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
            >
              Try Again
            </button>
            <button
              onClick={handleBack}
              className="w-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded flex items-center justify-center"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Packages
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto p-4 md:p-6 pb-24 md:pb-6">
        {/* Header */}
        <div className="mb-6">
          {/* <button
            onClick={handleBack}
            className="mb-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Packages
          </button> */}
          <div className="flex items-center mb-6">
            <div className="bg-orange-100 dark:bg-orange-900 text-orange-500 dark:text-orange-300 rounded-lg p-3 mr-4">
              <FileText className="h-6 w-6" />
            </div>
            <div>

              <div className="flex items-center space-x-2">
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white truncate max-w-[200px] md:max-w-[400px]">
                  #{packageData.trackingNo}
                </h1>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(packageData.trackingNo || '');
                  }}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  title="Copy tracking number"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                {packageData.description}
              </p>
            </div>
            <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium ${
              packageData.status === 'Ready' ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300' :
              packageData.status === 'Delivered' ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300' :
              'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
            }`}>
              {packageData.status}
            </span>
          </div>
          {isReviewsEnabled && packageData.status === 'Delivered' && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => navigate(`/reviews?packageId=${packageData.id}`)}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200"
                data-testid="rate-experience"
              >
                ★ Rate your experience
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Invoice Section */}
          {canHaveInvoice(packageData.status) && packageData.invoice && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div
                className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 md:px-6 py-4 flex justify-between items-center cursor-pointer"
                onClick={toggleInvoiceSection}
              >
                <div className="flex items-center">
                  <div className="bg-blue-100 dark:bg-blue-900 rounded-full p-2 mr-3">
                    <FileText className="h-5 w-5 text-blue-500 dark:text-blue-300" />
                  </div>
                  <div>
                    <h2 className="font-semibold dark:text-white">Invoice</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      #{packageData.manifestNumber || packageData.invoice.id} - ${packageData.invoice.amount?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <span className={`mr-3 text-sm px-2 py-1 rounded-full ${
                    packageData.invoice.status === 'PENDING_VERIFICATION'
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                      : packageData.invoice.status === 'Unpaid'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300'
                  }`}>
                    {packageData.invoice.status === 'PENDING_VERIFICATION'
                      ? 'Payment Under Review'
                      : packageData.invoice.status}
                  </span>
                  <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                    {invoiceExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                invoiceExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
              }`}>
                {/* Invoice Actions */}
                <div className="p-4 md:p-6">
                  {emailSent && (
                    <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300 p-4 rounded-md mb-4">
                      Invoice has been sent to your email successfully!
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={handleSendEmail}
                      disabled={isEmailSending}
                      className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded flex items-center justify-center shadow-sm disabled:bg-orange-300 dark:disabled:bg-orange-800"
                    >
                      {isEmailSending ? (
                        <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Mail className="h-5 w-5 mr-2" />
                      )}
                      SEND TO EMAIL
                    </button>
                    <a
                      href={`${import.meta.env.VITE_API_BASE_URL}/Invoices/preview?courierId=${packageData.courierId}&invoiceId=${packageData.invoice.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-medium py-3 px-4 rounded flex items-center justify-center shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <ExternalLink className="h-5 w-5 mr-2" />
                      VIEW INVOICE
                    </a>
                  </div>

                                    {/* Pay Invoice Button */}
                  {packageData.invoice.status === 'Unpaid' && hasPaymentGateway && (
                    <button
                      onClick={handlePayInvoice}
                      className="w-full mt-4 bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded flex items-center justify-center shadow-sm"
                    >
                      <CreditCard className="h-5 w-5 mr-2" />
                      PAY INVOICE
                    </button>
                  )}

                  {/* Invoice Details Toggle */}
                  <div className="mt-6">
                    <button
                      onClick={toggleInvoiceDetails}
                      className="w-full flex justify-between items-center text-left text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    >
                      <span className="font-medium">Invoice Details</span>
                      {invoiceDetailsExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </button>

                    {/* Invoice Details */}
                    <div className={`mt-4 transition-all duration-300 ease-in-out overflow-hidden ${
                      invoiceDetailsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Invoice Number:</span>
                          <span className="font-medium dark:text-white">#{packageData.manifestNumber || packageData.invoice.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Amount:</span>
                          <span className="font-medium dark:text-white">
                            ${packageData.invoice.amount?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Status:</span>
                          <span className={`font-medium ${
                            packageData.invoice.status === 'PENDING_VERIFICATION'
                              ? 'text-yellow-700 dark:text-yellow-300'
                              : packageData.invoice.status === 'Unpaid'
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {packageData.invoice.status === 'PENDING_VERIFICATION'
                              ? 'Payment Under Review'
                              : packageData.invoice.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attach Receipt Section */}
          {(!canHaveInvoice(packageData.status) || !packageData.invoice) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div
                className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 md:px-6 py-4 flex justify-between items-center cursor-pointer"
                onClick={toggleReceiptSection}
              >
                <h2 className="font-semibold dark:text-white">Attach Receipt</h2>
                <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                  {receiptExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
              </div>

              <div className={`transition-all duration-300 ease-in-out ${
                receiptExpanded ? 'max-h-none opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
              }`}>
                <div className="p-4 md:p-6">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Upload a receipt for this package to facilitate customs processing.
                  </p>

                  {uploadError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
                      {uploadError.message}
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* File Selection Section */}
                    <div>
                      <label className="cursor-pointer block w-full">
                        <div className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded flex items-center justify-center transition-colors">
                          <Upload className="h-5 w-5 mr-2" />
                          {selectedFiles.length > 0 ? 'Add More Files' : 'Select Receipt(s)'}
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          onChange={handleFileSelect}
                          accept=".pdf,.jpg,.jpeg,.png"
                          multiple
                        />
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                        {selectedFiles.length > 0
                          ? 'Click to add more files to your selection'
                          : 'You can select multiple files at once or add them one by one'
                        }
                      </p>
                    </div>

                    {/* Selected Files Section */}
                    {selectedFiles.length > 0 && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Selected Files ({selectedFiles.length})
                          </h4>
                          <button
                            onClick={() => setSelectedFiles([])}
                            className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Clear All
                          </button>
                        </div>

                        <InputField
                          darkMode={isDarkMode}
                          label='Receipt Amount (USD)'
                          type='number'
                          placeholder='Enter amount'
                          name='amount'
                          value={packageValue !== '' ? packageValue : ''}
                          disabled={false}
                          inputRef={amountInputRef}
                          onChange={(e) => {
                            const val = e.target.value === '' ? '' : Number(e.target.value);
                            setPackageValue(val);
                          }}
                        />

                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-between">
                              <div className="flex items-center flex-1 min-w-0">
                                <FileText className="h-5 w-5 mr-2 text-orange-500 flex-shrink-0" />
                                <div className="overflow-hidden">
                                  <span className="text-sm font-medium block truncate dark:text-white">
                                    {file.name}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {(file.size / 1024).toFixed(0)} KB
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                                className="ml-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 flex-shrink-0"
                                title="Remove file"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>

                        {/* Upload Button - Now more prominent */}
                        <div className="pt-4 pb-4">
                          <Button
                            onClick={handleUploadReceipt}
                            disabled={isUploading || selectedFiles.length === 0 || (packageValue === '' || packageValue <= 0)}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded transition-colors shadow-sm disabled:bg-orange-300 dark:disabled:bg-orange-800 flex items-center justify-center"
                          >
                            {isUploading ? (
                              <>
                                <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Uploading {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}...
                              </>
                            ) : (
                              `Upload ${selectedFiles.length} Receipt${selectedFiles.length > 1 ? 's' : ''}`
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Show a prompt when collapsed */}
              {!receiptExpanded && (
                <div className="px-6 py-3 text-sm text-blue-600 dark:text-blue-400 font-medium">
                  Click to upload your receipt and speed up customs processing
                </div>
              )}
            </div>
          )}

           {/* Attached Files Section */}
           {packageData.attachedFile && packageData.attachedFile.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden mt-6">
              <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 md:px-6 py-4">
                <h2 className="font-semibold dark:text-white">Attached Files</h2>
              </div>
              <div className="p-4 md:p-6">
                <div className="space-y-3">
                  {packageData.attachedFile.map((fileName, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-center overflow-hidden">
                        <FileText className="h-5 w-5 text-orange-500 flex-shrink-0 mr-3" />
                        <span className="text-sm font-medium truncate dark:text-white">
                          {fileName}
                        </span>
                      </div>
                      <div className="flex items-center ml-4">
                        <button
                          onClick={() => handleViewFile(fileName)}
                          className="text-blue-500 hover:text-blue-600 p-1"
                          title="View File"
                        >
                          <Download className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteFile(fileName)}
                          className="text-red-500 hover:text-red-600 p-1 ml-2"
                          title="Delete File"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 6h18"></path>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Package Details Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 md:px-6 py-4">
              <h2 className="font-semibold dark:text-white">Package Details</h2>
            </div>
            <div className="p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div className="overflow-hidden">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400">Description</h3>
                  <p className="font-medium dark:text-white truncate">{packageData.description}</p>
                </div>
                <div className="overflow-hidden">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400">Weight (lbs)</h3>
                  <p className="font-medium dark:text-white truncate">{formatWeightDisplay(packageData.weight, courier?.courierSetting?.weightRoundingMode, false)}</p>
                </div>
                <div className="overflow-hidden">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400">Merchant</h3>
                  <p className="font-medium dark:text-white truncate">{packageData.merchant || 'N/A'}</p>
                </div>
                <div className="overflow-hidden">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400">Package Value</h3>
                  <p className="font-medium dark:text-white truncate">
                    ${packageData.value?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="overflow-hidden">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400">Customs Fee</h3>
                  <p className="font-medium dark:text-white truncate">
                    ${packageData.customsFee?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="overflow-hidden">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400">Website URL</h3>
                  <p className="font-medium flex items-center dark:text-white">
                    <span className="truncate">{packageData.url || 'N/A'}</span>
                    {packageData.url && (
                      <a
                        href={packageData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-1 text-blue-500 hover:text-blue-600"
                      >
                        <ExternalLink className="h-4 w-4 flex-shrink-0" />
                      </a>
                    )}
                  </p>
                </div>
                <div className="overflow-hidden">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400">Created On</h3>
                  <p className="font-medium dark:text-white truncate">
                    {packageData.createdOn ? new Date(packageData.createdOn).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                <div className="overflow-hidden">
                  <h3 className="text-sm text-gray-500 dark:text-gray-400">Last Updated</h3>
                  <p className="font-medium dark:text-white truncate">
                    {packageData.lastUpdatedOn ? new Date(packageData.lastUpdatedOn).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
};

export default PackageDetail;
